import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "../../hooks/useTranslation";
import TruncatedTooltip from "./TruncatedTooltip";

/* Multi-select no padrão visual do mockup (fakeselect + popover):
   - value === null  => "Todos" (não filtra; o pai não envia o parâmetro)
   - value === [..]  => chaves selecionadas
   - master "Todos" no topo; busca opcional; swatch de cor opcional;
     sufixo "(inativo)" para usuários desativados. */
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const MultiSelectDropdown = ({
  options = [], // [{key, label, color?, inactive?}]
  value = null, // null = todos
  onChange,
  allLabel,
  withSwatch = false,
  searchable = false,
  disabled = false,
}) => {
  const { t } = useTranslation("consumption");
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  const effectiveAllLabel = allLabel || t("all");
  const isAll = value === null || value.length === options.length;
  const selectedSet = useMemo(
    () => new Set(value === null ? options.map((o) => o.key) : value),
    [value, options]
  );

  useEffect(() => {
    if (!open) return undefined;
    const handleClickAway = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickAway);
    return () => document.removeEventListener("mousedown", handleClickAway);
  }, [open]);

  const toggleAll = () => {
    onChange(isAll ? [] : null);
  };

  const toggleOption = (key) => {
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next.size === options.length ? null : Array.from(next));
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.trim().toLowerCase();
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, search]);

  const renderSummary = () => {
    if (isAll) return <span className="summary">{effectiveAllLabel}</span>;
    if (selectedSet.size === 0)
      return <span className="summary muted">{t("none")}</span>;
    if (selectedSet.size === 1) {
      const opt = options.find((o) => selectedSet.has(o.key));
      return (
        <span className="summary">
          {withSwatch && opt?.color && (
            <span className="swatch" style={{ background: opt.color }} />
          )}
          <TruncatedTooltip text={opt?.label || ""} className="summary-text" />
        </span>
      );
    }
    return (
      <span className="summary">
        {t("selected")} <span className="pill-count">{selectedSet.size}</span>
      </span>
    );
  };

  return (
    <div className={`dd ${open ? "open" : ""}`} ref={rootRef}>
      <div
        className={`fakeselect ${disabled ? "disabled" : ""}`}
        tabIndex={0}
        onClick={() => !disabled && setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        role="button"
        aria-expanded={open}
      >
        {renderSummary()}
        <span className="ico"><ChevronIcon /></span>
      </div>
      {open && (
        <div className="dd-pop">
          {searchable && (
            <div className="dd-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
              </svg>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("search")}
                autoFocus
              />
            </div>
          )}
          <div
            className={`dd-opt master ${isAll ? "on" : ""}`}
            onClick={toggleAll}
          >
            <span className="box"><CheckIcon /></span>
            {effectiveAllLabel}
          </div>
          {filtered.map((opt) => (
            <div
              key={opt.key}
              className={`dd-opt ${selectedSet.has(opt.key) ? "on" : ""}`}
              onClick={() => toggleOption(opt.key)}
            >
              <span className="box"><CheckIcon /></span>
              {withSwatch && opt.color && (
                <span className="swatch" style={{ background: opt.color }} />
              )}
              <TruncatedTooltip text={opt.label} className="dd-opt-label" />
              {opt.inactive && (
                <span className="tag-inactive">{t("inactive")}</span>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="dd-empty">{t("noOptions")}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
